import React, { useMemo } from 'react';
import { styled } from '@mui/material';
import { sanitizeHtml, SanitizationOptions } from '../../utils/htmlSanitizer';

interface SafeHTMLRendererProps {
  content: string;
  hints?: SanitizationOptions;
  className?: string;
}

const StyledHTMLContainer = styled('div')(({ theme }) => ({
  // Set default text color for all content
  color: theme.palette.text.primary,
  '& *': {
    maxWidth: '100%',
    wordWrap: 'break-word',
    // Ensure text color is inherited unless specifically overridden
    color: 'inherit',
  },
  '& img': {
    height: 'auto',
    display: 'block',
    margin: theme.spacing(2, 0),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    boxShadow: theme.shadows[1],
    color: theme.palette.text.primary,
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    textAlign: 'left',
    color: theme.palette.text.primary,
  },
  '& th': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : theme.palette.grey[200],
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.03)',
  },
  '& pre': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  '& code': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)',
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.shape.borderRadius / 2,
    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    fontSize: '0.9em',
    color: theme.palette.mode === 'dark'
      ? '#4DC4D2' // Turquoise for dark mode
      : '#325BA8', // Deep blue for light mode
  },
  '& pre code': {
    backgroundColor: 'transparent',
    padding: 0,
    color: theme.palette.text.primary,
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginLeft: 0,
    paddingLeft: theme.spacing(2),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
  '& h1': { fontSize: '2rem' },
  '& h2': { fontSize: '1.75rem' },
  '& h3': { fontSize: '1.5rem' },
  '& h4': { fontSize: '1.25rem' },
  '& h5': { fontSize: '1.125rem' },
  '& h6': { fontSize: '1rem' },
  '& p': {
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
  },
  '& ul, & ol': {
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(3, 0),
  },
}));

const SafeHTMLRenderer: React.FC<SafeHTMLRendererProps> = ({ content, hints, className }) => {
  const sanitizedHTML = useMemo(() => {
    return sanitizeHtml(content, hints || {});
  }, [content, hints]);

  return (
    <StyledHTMLContainer
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  );
};

export default SafeHTMLRenderer;